import type { DashboardKpis } from '../../types'

// KpiRow - four KPI cards. Each has a left-edge accent stripe that maps to
// the metric's semantic colour (live = oxblood, upcoming = brass, this-year =
// slate, build progress = green). Skeleton state shows the structure with
// em-dash placeholders so the page doesn't reflow when data arrives.

interface Props {
  kpis: DashboardKpis | undefined
  loading: boolean
}

export function KpiRow({ kpis, loading }: Props) {
  return (
    <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
      <KpiCard label="Live now" value={kpis?.liveEvents} loading={loading} accent="oxblood" suffix="event" />
      <KpiCard label="Upcoming (14 days)" value={kpis?.upcoming14d} loading={loading} accent="brass" suffix="event" />
      <KpiCard label="Events this year" value={kpis?.eventsThisYear} loading={loading} accent="slate" suffix="total" />
      <KpiCard label="Avg build progress" value={kpis?.avgBuildProgress} loading={loading} accent="green" suffix="%" raw />
    </div>
  )
}

interface CardProps {
  label: string
  value: number | undefined
  loading: boolean
  accent: 'oxblood' | 'brass' | 'slate' | 'green'
  suffix?: string
  raw?: boolean    // raw = render suffix as literal (e.g. "%") rather than "value suffix"
}

const ACCENTS: Record<CardProps['accent'], string> = {
  oxblood: '#7a2329',
  brass: '#9b7f2a',
  slate: '#3d5a6c',
  green: '#0a4a3a'
}

function KpiCard({ label, value, loading, accent, suffix, raw }: CardProps) {
  const display = loading || value == null ? '—' : value.toString()
  return (
    <div
      className="relative bg-white border border-line rounded-lg overflow-hidden"
      style={{ padding: '18px 20px' }}
    >
      <span
        aria-hidden
        className="absolute left-0 top-0 bottom-0"
        style={{ width: 3, background: ACCENTS[accent] }}
      />
      <div className="text-[11.5px] uppercase tracking-wider font-semibold text-ink-soft mb-1.5">
        {label}
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="font-display text-3xl font-bold leading-none">{display}</span>
        {!loading && value != null && suffix && (
          <span className="text-xs text-ink-soft">
            {raw ? suffix : suffix + (value === 1 ? '' : suffix.endsWith('s') ? '' : 's')}
          </span>
        )}
      </div>
    </div>
  )
}
